import {ChangeDetectionStrategy, Component, inject, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {TranslationService} from '../../../services/translation.service';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoginService} from '../login.service';
import {SsoConfiguration} from '../sso-configuration';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {LoadingSpinnerComponent} from '../../loading-spinner/loading-spinner.component';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {ApplyIfNotEmptyDirective} from '../../../directives/apply-if-not-empty.directive';

@Component({
  selector: 'app-login-box',
  templateUrl: './login-box.component.html',
  styleUrls: ['./login-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    LoadingSpinnerComponent,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatLabel,
    MatError,
    ApplyIfNotEmptyDirective
  ],
  standalone: true
})
export class LoginBoxComponent implements OnInit {
  translationService = inject(TranslationService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject<MatDialogRef<LoginBoxComponent>>(MatDialogRef);
  private readonly loginService = inject(LoginService);
  private readonly sanitizer = inject(DomSanitizer);
  data = inject<{ url: string; }>(MAT_DIALOG_DATA);
  private readonly platformId = inject<Object>(PLATFORM_ID);

  loginForm: FormGroup;
  loading: boolean = false;
  error: string;
  ssoFormPropagatedHeight: string = 'inherit';

  ssoConfiguration: SsoConfiguration;

  private static EventListenerAdded = false;
  private static DialogRef : MatDialogRef<LoginBoxComponent> = null;

  constructor(){
    LoginBoxComponent.DialogRef = this.dialogRef;
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required]
    });

    this.ssoConfiguration = this.loginService.getSsoConfiguration();

    if (isPlatformBrowser(this.platformId) && !LoginBoxComponent.EventListenerAdded) {
      LoginBoxComponent.EventListenerAdded = true;
      window.addEventListener(
        "message",
        (event) => {
          const eventData = typeof event.data == 'string' ? JSON.parse(event.data) : event.data;

          if (eventData.eventNature == 'ssoAjaxViewLoaded') {
            this.ssoFormPropagatedHeight = eventData.formHeight ? (eventData.formHeight + 5) + 'px' : 'inherit';
            // TPL: property-binding does not work at all - just got it work in old fashion way ...
            document.getElementById('loginBoxIFrame').style.height = this.ssoFormPropagatedHeight;
          } else {
            const alreadyLoggedIn = eventData.code === 2000;
            const justLoggedIn = typeof eventData.code == 'string' && eventData.code.match(/^[0-9a-f]{16,}$/);

            if (alreadyLoggedIn || justLoggedIn) {
              this.onClose();
              this.loginService.checkStatus();
            } else {
              this.loginService.resetData();
            }
          }
        },
        false,
      );
    }
  }

  hassoLoginBoxSrc(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl([
      this.ssoConfiguration.ssoHost + '/auth/authorize',
      '?client_id=' + this.ssoConfiguration.clientId,
      '&return_uri=' + encodeURIComponent(this.ssoConfiguration.returnUrl),
      '&responsetype=event',
      '&domain=' + encodeURIComponent(window?.location.origin),
      '&ajaxMode=1' +
      '&formId=Login'
    ].join(''));
  }

  onLogin(): void {
    if (this.loginForm.valid) {
      this.loading = true;

      if (!this.data?.url) {
        this.data = {url: ''};
      }

      this.loginService.login(this.loginForm.get('email').value, this.loginForm.get('password').value, this.data.url).subscribe({
        next: res => {
          if (res && res.token !== null && res.token !== 0 && res.status !== 'error') {
            this.onClose();
          } else {
            this.error = res.message ? res.message : 'Bei der Anmeldung ist ein Fehler aufgetreten.';
            this.loading = false;
          }
        },
        error: httpError => {
          this.error = httpError.error.message;
          this.loading = false;
        }
      });
    }
  }

  onClose(): void {
    LoginBoxComponent.DialogRef?.close();
  }
}
